import { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "./supabase";
import { apiGet } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCandidate = async (accessToken) => {
    try {
      const data = await apiGet("/student/me", accessToken);
      if (data.exists && data.candidate) {
        setCandidate(data.candidate);
      } else {
        setCandidate(null);
      }
    } catch (err) {
      console.error("Failed to fetch candidate:", err);
      setCandidate(null);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const demoOtp = params.get("demo_otp");
    const demoEmail = params.get("demo_email");

    const initSession = async () => {
      if (demoOtp && demoEmail) {
        // Clean URL immediately so OTP doesn't linger
        params.delete("demo_otp");
        params.delete("demo_email");
        params.delete("demo");
        const clean = params.toString();
        window.history.replaceState({}, "", clean ? `?${clean}` : window.location.pathname);

        const { error } = await supabase.auth.verifyOtp({ email: demoEmail, token: demoOtp, type: "email" });
        if (error) console.error("Demo OTP verify failed:", error);
      }

      const { data: { session: s } } = await supabase.auth.getSession();
      setSession(s);
      if (s?.access_token) {
        await fetchCandidate(s.access_token);
      }
      setLoading(false);
    };

    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, s) => {
        setSession(s);
        if (s?.access_token) {
          await fetchCandidate(s.access_token);
        } else {
          setCandidate(null);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Passwordless email sign-in for students.
  // shouldCreateUser is intentionally left at its default (true) — students
  // self-onboard by following the school's invite link, so a new auth user
  // is minted on first magic-link click. The careers admin controls who
  // enters the pool via the candidates table, not via Supabase Auth.
  const sendMagicLink = async (email) => {
    const redirectTo = window.location.origin;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo },
    });
    if (error) throw error;
  };

  const demoLogin = async () => {
    const apiUrl = import.meta.env.VITE_API_URL || "https://relay-backend-app.fly.dev";
    const res = await fetch(`${apiUrl}/demo/student-token`);
    if (!res.ok) throw new Error("Demo login failed");
    const { otp, email } = await res.json();
    const { error } = await supabase.auth.verifyOtp({ email, token: otp, type: "email" });
    if (error) throw error;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setCandidate(null);
  };

  return (
    <AuthContext.Provider value={{
      session,
      candidate,
      loading,
      sendMagicLink,
      demoLogin,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
